package pl.decks.personal.pages.blog

import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.StyleVariable
import com.varabyte.kobweb.compose.css.TransitionTimingFunction
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.extendedByBase
import com.varabyte.kobweb.silk.style.selectors.active
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.selectors.link
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.px
import pl.decks.personal.components.layouts.site.SiteLayoutStylesheet
import pl.decks.personal.components.widgets.splitcard.SplitCardStylesheet
import pl.decks.personal.components.widgets.tag.TagStylesheet
import pl.decks.personal.extensions.lineClamp
import pl.decks.personal.extensions.toSitePalette
import pl.decks.personal.styles.FontTypography
import pl.decks.personal.styles.SiteLayout

object BlogListingStylesheet {
    object Vars {
        val emphasisedTextColor by StyleVariable<Color>()
        val imageZoom by StyleVariable(1.0)
    }

    object Header {
        val Container = CssStyle.base {
            Modifier.fillMaxWidth().padding(SiteLayout.Space.none).gap(SiteLayout.Space.Medium)
        }

        val Title = FontTypography.sansSerif.extendedByBase {
            Modifier.fontSize(FontTypography.Size.XXLarge).fontWeight(FontTypography.Weight.Bold)
        }
    }

    object Card {
        val Listing = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .padding(leftRight = SiteLayout.Space.XLarge, topBottom = SiteLayout.Space.Medium)
                .gap(SiteLayout.Space.XXLarge)
        }
        val Entry = CssStyle {
            base {
                Modifier.fillMaxWidth().padding(bottom = SiteLayout.Space.XSmall)
            }
            hover {
                Modifier
                    .setVariable(Vars.emphasisedTextColor, SplitCardStylesheet.Vars.cardAccentColor.value())
                    .setVariable(Vars.imageZoom, 1.05)
            }
            active {
                Modifier
                    .setVariable(Vars.emphasisedTextColor, SplitCardStylesheet.Vars.cardAccentColor.value())
                    .setVariable(Vars.imageZoom, 1.05)
            }
        }
        val Title = CssStyle {
            base {
                Modifier
                    .fillMaxWidth()
                    .padding(SiteLayout.Space.XSmall)
                    .fontSize(FontTypography.Size.Large)
                    .lineHeight(FontTypography.LineHeight.Small)
                    .fontWeight(FontTypography.Weight.Medium)
                    .color(Vars.emphasisedTextColor.value(colorMode.toPalette().color))
                    .transition {
                        property("color")
                        timingFunction(TransitionTimingFunction.EaseInOut)
                        duration(SplitCardStylesheet.Vars.secondaryTransitionDuration.value())
                    }
            }
            link {
                Modifier.color(Vars.emphasisedTextColor.value(colorMode.toPalette().color))
            }
        }
        val Subscript = CssStyle.base {
            Modifier.fillMaxWidth().padding(SiteLayout.Space.XSmall)
        }
        val Authorship = CssStyle.base {
            Modifier
                .fillMaxWidth()
                .color(Vars.emphasisedTextColor.value(colorMode.toPalette().color))
                .transition {
                    property("color")
                    timingFunction(TransitionTimingFunction.EaseInOut)
                    duration(SplitCardStylesheet.Vars.secondaryTransitionDuration.value())
                }
        }
        val Description = CssStyle.base {
            Modifier.fillMaxWidth().lineClamp(3)
        }
        val ImageContainer = CssStyle.base {
            Modifier.fillMaxWidth().maxHeight(150.px).overflow(Overflow.Hidden)
        }
        val Image = CssStyle.base {
            Modifier
                .scale(Vars.imageZoom.value(1.0))
                .objectFit(ObjectFit.Cover)
                .transition {
                    property("scale")
                    timingFunction(TransitionTimingFunction.EaseInOut)
                    duration(SplitCardStylesheet.Vars.secondaryTransitionDuration.value())
                }
        }
    }

    val Page = SiteLayoutStylesheet.Page.Content.extendedByBase {
        val sitePalette = colorMode.toSitePalette()
        val accentPrimary = sitePalette.accents.primary.toRgb()
        val accentSecondary = sitePalette.accents.secondary.toRgb()
        Modifier
            .setVariable(SplitCardStylesheet.Vars.cardAccentColor, accentPrimary)
            .setVariable(TagStylesheet.Vars.textColor, accentSecondary)
            .setVariable(TagStylesheet.Vars.textInteractionColor, accentSecondary.copyf(alpha = 0.75f))
            .setVariable(TagStylesheet.Vars.textInactiveColor, accentSecondary.copyf(alpha = 0.5f))
            .setVariable(TagStylesheet.Vars.chipOutlineColor, accentSecondary)
            .setVariable(TagStylesheet.Vars.chipHoverBackgroundColor, accentSecondary.copyf(alpha = 0.25f))
            .setVariable(TagStylesheet.Vars.chipActiveBackgroundColor, accentSecondary.copyf(alpha = 0.5f))
            .setVariable(TagStylesheet.Vars.chipSelectedBackgroundColor, accentSecondary.copyf(alpha = 0.75f))
    }
}
